import clsx from 'clsx';
import { useCallback, useRef, useState } from 'react';
import { LazyLoadComponent } from 'react-lazy-load-image-component';

import Loader from '~components/generic/loader/Loader';
import { getAbsoluteUrl } from '~store/getter';
import CustomMoodboardPictureStyles from './CustomMoodboardPicture.css';

type Position = {
  x: number;
  y: number;
};

export type CustomMoodboardPictureProps = {
  proportions: string;
  data: Entity;
  origData: string;
  placeholder?: boolean;
  lazyLoad?: boolean;
  showOriginal?: boolean;
  editing: boolean;
  onChangePosition: (position: Position) => void;
  imagePosition: string;
  borderRadius: number;
};

export const CustomMoodboardPicture = ({
  data,
  editing,
  origData,
  proportions,
  placeholder,
  lazyLoad,
  onChangePosition,
  imagePosition,
  borderRadius,
}: CustomMoodboardPictureProps) => {
  let classes = CustomMoodboardPictureStyles.container;
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const imageRef = useRef<HTMLImageElement>(null);
  const position = JSON.parse(imagePosition as string);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!editing) return;
      e.preventDefault();
      setIsDragging(true);
      const rect = imageRef.current?.getBoundingClientRect();
      if (!rect) return;

      setStartPos({
        x: e.clientX - ((100 - position.x) * rect.width) / 100,
        y: e.clientY - ((100 - position.y) * rect.height) / 100,
      });
    },
    [editing, position],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!editing || !isDragging) return;
      e.preventDefault();

      const rect = imageRef.current?.getBoundingClientRect();
      if (!rect) return;

      requestAnimationFrame(() => {
        const x = ((e.clientX - startPos.x) / rect.width) * 100;
        const y = ((e.clientY - startPos.y) / rect.height) * 100;

        onChangePosition({
          x: 100 - Math.min(Math.max(x, 0), 100),
          y: 100 - Math.min(Math.max(y, 0), 100),
        });
      });
    },
    [editing, isDragging, startPos, onChangePosition],
  );

  const handleMouseUp = useCallback(() => {
    if (!editing) return;
    setIsDragging(false);
  }, [editing]);

  if (!data && origData) {
    return (
      <div className={classes}>
        <Loader absoluteCenter />
      </div>
    );
  }

  if (!data && !origData && placeholder) {
    return (
      <div className={classes}>
        <div className="c-image-placeholder" />
      </div>
    );
  }

  if (!data || !data.attributes) return '';

  const borderClasses = clsx({
    'rounded-none': borderRadius === 0,
    rounded: borderRadius === 1,
    'rounded-sm': borderRadius === 2,
    'rounded-md': borderRadius === 3,
    'rounded-lg': borderRadius === 4,
  });

  const content = (
    <picture
      style={{
        cursor: editing ? 'move' : 'default',
        userSelect: 'none',
        touchAction: 'none',
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <img
        ref={imageRef}
        alt=""
        src={getAbsoluteUrl(data.attributes.url)}
        className={`${borderClasses}   o-image u-fullwidth ${editing ? CustomMoodboardPictureStyles.editMode : ''}`}
        style={{
          aspectRatio: proportions.replace(':', '/'),
          objectFit: 'cover',
          objectPosition: `${position.x}% ${position.y}%`,
          userSelect: 'none',
          willChange: editing ? 'object-position' : 'auto',
          transition: isDragging ? 'none' : 'object-position 0.2s ease-out',
        }}
        draggable={false}
      />
    </picture>
  );

  if (lazyLoad) {
    return <LazyLoadComponent threshold={300}>{content}</LazyLoadComponent>;
  }
  return content;
};
